import {Observable} from 'rxjs';
import {CpmCUserRoleApiModel} from '../../models/c-common/cpmc-user-role.api-model';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';
import {
    CpmCSdkUserRoleCreateApiSchema,
    CpmCSdkUserRoleUpdateApiSchema
} from '../../schemas/cpmc-sdk-user-role.api-schemas';

export type CpmCGetUserRolesParams = {
    [K in keyof CpmCUserRoleApiModel]?: CpmCUserRoleApiModel[K] extends AllowedQueryParamTypes
        ? CpmCUserRoleApiModel[K]
        : never;
} & {
    includeAll?: boolean;
} & QueryParams;

/**
 * UserRole Api Service
 */
export class CpmCUserRoleApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'name', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        if (inCustomerUUID !== undefined || (inCustomerUUID === undefined && customerUUID)) {
            return base + `customer/${inCustomerUUID || customerUUID}/user-role`;
        } else {
            return base + 'user-role';
        }
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCUserRoleApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCUserRoleApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, userRoleUUID: string): string {
        return `${CpmCUserRoleApiService.baseUrl(inCustomerUUID)}/${userRoleUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, userRoleUUID: string): string {
        return `${CpmCUserRoleApiService.baseUrl(inCustomerUUID)}/${userRoleUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, userRoleUUID: string): string {
        return `${CpmCUserRoleApiService.baseUrl(inCustomerUUID)}/${userRoleUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getUserRoles(
        params: CpmCGetUserRolesParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCUserRoleApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCUserRoleApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCUserRoleApiModel>>(
            CpmCUserRoleApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getUserRoleByUUID(userRoleUUID: string): Observable<CpmCUserRoleApiModel> {
        return CpmCApiBridge.httpGet<CpmCUserRoleApiModel>(CpmCUserRoleApiService.endpointGet(undefined, userRoleUUID));
    }
    public static createUserRole(payload: CpmCSdkUserRoleCreateApiSchema): Observable<CpmCUserRoleApiModel> {
        return CpmCApiBridge.httpPost<CpmCUserRoleApiModel>(CpmCUserRoleApiService.endpointNew(undefined), payload);
    }
    public static updateUserRole(
        userRoleUUID: string,
        payload: CpmCSdkUserRoleUpdateApiSchema
    ): Observable<CpmCUserRoleApiModel> {
        return CpmCApiBridge.httpPut<CpmCUserRoleApiModel>(
            CpmCUserRoleApiService.endpointEdit(undefined, userRoleUUID),
            payload
        );
    }
    public static deleteUserRole(userRoleUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(CpmCUserRoleApiService.endpointDelete(undefined, userRoleUUID));
    }
}
